import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useFetcher, useNavigate, useRouteLoaderData } from 'react-router'
import { type loader as rootLoader } from '#app/root.tsx'
import { type loader as notificationsLoader } from '#app/routes/resources/notifications.ts'
import { formatRelativeTime, useLocale } from '#app/utils/intl.ts'
import { cn } from '#app/utils/misc.tsx'
import { Button } from '../ui/button.tsx'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '../ui/dropdown-menu.tsx'
import { Icon } from '../ui/icon.tsx'
import { Skeleton } from '../ui/skeleton.tsx'
import { notificationTitle } from './notification-labels.ts'
import { useNotificationStream } from './notification-stream.tsx'

/** The resource route the dropdown loads from and posts its mark-read intents to. */
const NOTIFICATIONS_ROUTE = '/resources/notifications'

/** Above this the badge collapses to `9+` (ADR-078 chosen direction). */
const BADGE_CAP = 9

/** A notification row as the serialized loader hands it to the client. */
type ClientNotification = Awaited<
	ReturnType<typeof notificationsLoader>
>['data']['notifications'][number]

/**
 * The navbar notification bell (EPT-157, ADR-078). An icon trigger carrying the
 * unread count as an overlaid {@link UnreadBadge}, opening a dropdown of the
 * recent notifications. The count is SSR-fed by the root loader so the badge is
 * right on first paint and refreshes on navigation; opening the menu loads the
 * recent rows via a fetcher. Live SSE delivery + arrival toast layer on in
 * EPT-160 — here the data refreshes on navigation and on open only.
 *
 * Rendered only for a signed-in user on the `full`/`marketing` shells (the
 * AppShell gates it on `nav.account === 'avatar'`), never on `minimal` (auth).
 */
export function NotificationBell() {
	const { t } = useTranslation()
	const rootData = useRouteLoaderData<typeof rootLoader>('root')
	const listFetcher = useFetcher<typeof notificationsLoader>()
	const markFetcher = useFetcher()
	const stream = useNotificationStream()
	const [open, setOpen] = useState(false)

	// The badge count: the live SSE tally once the stream connects (so an arrival
	// bumps it with no navigation), else the freshly-loaded dropdown tally, else
	// the SSR root-loader value. A pending mutation is reflected optimistically so
	// the badge reacts before the server round-trip settles.
	const loadedUnread = listFetcher.data?.unreadCount
	const baseUnread = stream.liveCount ?? loadedUnread ?? rootData?.unreadCount ?? 0
	const pendingIntent = markFetcher.formData?.get('intent')
	const unreadCount =
		pendingIntent === 'mark-all-read'
			? 0
			: pendingIntent === 'mark-read'
				? Math.max(0, baseUnread - 1)
				: baseUnread

	const notifications = listFetcher.data?.notifications ?? []
	// The menu has never resolved data yet: show skeletons on first open.
	const isLoading = listFetcher.state === 'loading' && !listFetcher.data

	function handleOpenChange(next: boolean) {
		setOpen(next)
		// Refresh on every open so a menu re-opened after an action is current.
		if (next) void listFetcher.load(NOTIFICATIONS_ROUTE)
	}

	function markAllRead() {
		// Keep the live SSE count coherent: reads never travel over the stream, so
		// clear it here or a later arrival would re-inflate the badge (EPT-160).
		stream.applyLocalRead('all')
		void markFetcher.submit(
			{ intent: 'mark-all-read' },
			{ method: 'POST', action: NOTIFICATIONS_ROUTE },
		)
	}

	const label =
		unreadCount > 0
			? t('notifications.bell.labelUnread', { count: unreadCount })
			: t('notifications.bell.label')

	return (
		<DropdownMenu open={open} onOpenChange={handleOpenChange}>
			<DropdownMenuTrigger asChild>
				<Button
					variant="outline"
					size="icon"
					// A 36×36 ringed circle with the glyph inset; the `before` overlay
					// widens the pointer hit target to ≥44px without growing the visual.
					className="relative size-9 rounded-full before:absolute before:-inset-1 before:content-['']"
					aria-label={label}
				>
					<Icon name="bell" className="size-[17px]" aria-hidden />
					{unreadCount > 0 ? (
						// Keyed on the count so a live arrival remounts the badge and replays
						// the bump; the animation self-disables under reduced motion.
						<UnreadBadge key={unreadCount} count={unreadCount} />
					) : null}
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent
				align="end"
				sideOffset={8}
				// Below `md` a full 360px panel fills the phone and sits flush to the
				// edge — cap it to the viewport (less a gutter) and keep Radix a
				// collision margin so it never touches the screen edge.
				collisionPadding={12}
				className="w-[min(360px,calc(100vw-1.5rem))] rounded-xl p-0"
			>
				<div className="border-border flex items-center justify-between border-b px-3 py-2">
					<span className="text-body-sm font-semibold">
						{t('notifications.drop.title')}
					</span>
					{unreadCount > 0 ? (
						<Button
							variant="ghost"
							size="sm"
							className="text-primary h-auto px-2 py-1"
							onClick={markAllRead}
						>
							{t('notifications.drop.markAllRead')}
						</Button>
					) : null}
				</div>

				{isLoading ? (
					<NotificationSkeleton label={t('notifications.drop.loading')} />
				) : notifications.length === 0 ? (
					<NotificationEmpty
						title={t('notifications.drop.empty.title')}
						body={t('notifications.drop.empty.body')}
					/>
				) : (
					<div className="max-h-[392px] overflow-y-auto py-1">
						{notifications.map((notification) => (
							<NotificationRow
								key={notification.id}
								notification={notification}
								optimisticRead={
									pendingIntent === 'mark-all-read' ||
									(pendingIntent === 'mark-read' &&
										markFetcher.formData?.get('notificationId') ===
											notification.id)
								}
								onSelect={() => setOpen(false)}
								onLocalRead={() => stream.applyLocalRead('one')}
								markFetcher={markFetcher}
							/>
						))}
					</div>
				)}

				<DropdownMenuItem
					asChild
					className="text-primary border-border justify-center border-t py-2 text-body-xs font-medium"
				>
					<Link to="/notifications">{t('notifications.drop.viewAll')}</Link>
				</DropdownMenuItem>
			</DropdownMenuContent>
		</DropdownMenu>
	)
}

/**
 * The overlaid unread counter — a small `primary` pill pinned to the bell's
 * top-right, ringed in the page background so it reads as a badge sitting *on*
 * the icon. Deliberately not the DS `Badge` (its inline padding/type variants
 * fight a 17px circular counter). `aria-hidden`: the count already rides the
 * bell's accessible label, so a screen reader shouldn't read it twice.
 */
export function UnreadBadge({ count }: { count: number }) {
	const display = count > BADGE_CAP ? `${BADGE_CAP}+` : String(count)
	return (
		<span
			aria-hidden
			className="bg-primary text-primary-foreground pointer-events-none absolute -top-1 -right-1 inline-flex min-w-[17px] items-center justify-center rounded-full px-1 text-body-2xs font-semibold tabular-nums ring-2 ring-background animate-notif-bump motion-reduce:animate-none"
		>
			{display}
		</span>
	)
}

/**
 * One dropdown row (EPT-157). Unread is signalled by both a leading `primary`
 * dot **and** a heavier title weight — never colour alone (`epic-ui-guidelines`).
 * Selecting a row marks it read (optimistic fetcher) and navigates to its
 * `linkTo` target, then closes the menu.
 */
function NotificationRow({
	notification,
	optimisticRead,
	onSelect,
	onLocalRead,
	markFetcher,
}: {
	notification: ClientNotification
	optimisticRead: boolean
	onSelect: () => void
	onLocalRead: () => void
	markFetcher: ReturnType<typeof useFetcher>
}) {
	const { t } = useTranslation()
	const locale = useLocale()
	const navigate = useNavigate()
	const now = new Date()
	const isRead = notification.readAt != null || optimisticRead

	const title = notificationTitle(t, notification.type, notification.params)
	const time = formatRelativeTime(locale, notification.createdAt, now)
	const stateWord = t(
		isRead ? 'notifications.drop.read' : 'notifications.drop.unread',
	)

	function handleSelect() {
		if (!isRead) {
			onLocalRead()
			void markFetcher.submit(
				{ intent: 'mark-read', notificationId: notification.id },
				{ method: 'POST', action: NOTIFICATIONS_ROUTE },
			)
		}
		onSelect()
		if (notification.linkTo) void navigate(notification.linkTo)
	}

	return (
		<DropdownMenuItem
			onSelect={handleSelect}
			aria-label={`${title} · ${stateWord} · ${time}`}
			className="flex items-center gap-2 px-3 py-2"
		>
			<span
				aria-hidden
				className={cn(
					'size-[7px] shrink-0 rounded-full',
					isRead ? 'bg-transparent' : 'bg-primary',
				)}
			/>
			<span
				className={cn(
					'flex-1 truncate text-body-sm',
					isRead
						? 'text-muted-foreground font-normal'
						: 'text-foreground font-semibold',
				)}
			>
				{title}
			</span>
			<span className="text-muted-foreground shrink-0 text-body-2xs">
				{time}
			</span>
		</DropdownMenuItem>
	)
}

/** Loading affordance: skeleton rows in a disabled, `aria-busy` region. */
function NotificationSkeleton({ label }: { label: string }) {
	return (
		<div aria-busy className="space-y-1 py-1" aria-label={label}>
			{Array.from({ length: 3 }).map((_, index) => (
				<div key={index} className="flex items-center gap-2 px-3 py-2">
					<Skeleton className="size-[7px] rounded-full" />
					<Skeleton className="h-3.5 flex-1" />
					<Skeleton className="h-3 w-8" />
				</div>
			))}
		</div>
	)
}

/** The all-caught-up empty state — a brand-soft tile with a bell glyph. */
function NotificationEmpty({ title, body }: { title: string; body: string }) {
	return (
		<div className="flex flex-col items-center gap-2 px-6 py-8 text-center">
			<span className="bg-brand-soft text-brand flex size-11 items-center justify-center rounded-full">
				<Icon name="bell" className="size-[21px]" aria-hidden />
			</span>
			<p className="text-body-sm font-semibold">{title}</p>
			<p className="text-muted-foreground text-body-xs">{body}</p>
		</div>
	)
}
